import {
  ControlZone,
  ControlZoneSource,
  CONTROL_HEALTH_MESSAGES,
  Healthcheck,
  HEALTHCHECK_NAMES,
  HEALTH_STATUS,
  ZONE_KEYS,
} from 'domain/shared/newProject';
import { commercialHealthcheckNames } from 'domain/projects/newProject/controls/commercial/CommercialControl';
import { engineeringHealthcheckNames } from 'domain/projects/newProject/controls/engineering/EngineeringControl';
import { projectManagementHealthcheckNames } from 'domain/projects/newProject/controls/projectManagement/ProjectManagementControl';
import {
  staffingHealthcheckNames,
  staffingRiskIssueTypes,
} from 'domain/projects/newProject/controls/staffing/StaffingControl';
import { resolveControlsZone, normalizeControlZone } from 'domain/projects/newProject/controls/utils';
import { HEALTHCHECK_LABELS } from 'domain/projects/newProject/controls/commercial/constants';
import { StaffingRiskIssue } from 'domain/shared/staffing';

type ControlIssueKey = keyof typeof CONTROL_HEALTH_MESSAGES;

export type ControlsIssueSource = {
  health?: {
    healthChecks?: Healthcheck[];
  } | null;
  staffingRisk?: {
    issues?: StaffingRiskIssue[];
  } | null;
};

type SubIssue = {
  label: string;
  description?: string;
  zone: ControlZone;
};

export type ControlIssue = {
  key: ControlIssueKey;
  message: string;
  zone: ControlZone;
  subIssues: SubIssue[];
};

const HEALTHCHECK_NAME_TO_LABEL: Partial<Record<string, string>> = {
  [HEALTHCHECK_NAMES.PROJECT_COMMERCIAL_STATE]: HEALTHCHECK_LABELS.COMMERCIAL_STATE,
  [HEALTHCHECK_NAMES.COMMERCIAL_RISKS]: HEALTHCHECK_LABELS.COMMERCIAL_RISKS,
  [HEALTHCHECK_NAMES.ASSUMPTIONS]: HEALTHCHECK_LABELS.ASSUMPTION,
  [HEALTHCHECK_NAMES.CONTRACT_RENEWAL]: HEALTHCHECK_LABELS.SOW_EXPIRES,
  [HEALTHCHECK_NAMES.BUDGET_STATE]: HEALTHCHECK_LABELS.BUDGET,
  [HEALTHCHECK_NAMES.BILLING]: HEALTHCHECK_LABELS.BILLING,
  [HEALTHCHECK_NAMES.RISKS]: HEALTHCHECK_LABELS.RISKS,
  [HEALTHCHECK_NAMES.PPC]: HEALTHCHECK_LABELS.PPC,
  [HEALTHCHECK_NAMES.ENGINEERING_BUGS]: HEALTHCHECK_LABELS.PRODUCTION_BUGS,
  [HEALTHCHECK_NAMES.ENGINEERING_RISKS]: HEALTHCHECK_LABELS.RISKS,
  [HEALTHCHECK_NAMES.ENGINEERING_INCIDENTS]: HEALTHCHECK_LABELS.PRODUCTION_INCIDENTS,
  [HEALTHCHECK_NAMES.ENGINEERING_SETUP]: HEALTHCHECK_LABELS.PRODUCTION_OWNERSHIP,
  [HEALTHCHECK_NAMES.BUSINESS_OUTCOMES]: HEALTHCHECK_LABELS.BUSINESS_OUTCOMES,
  [HEALTHCHECK_NAMES.PROJECT_MANAGEMENT_RISKS]: HEALTHCHECK_LABELS.RISKS,
  [HEALTHCHECK_NAMES.MILESTONES]: HEALTHCHECK_LABELS.MILESTONES,
  [HEALTHCHECK_NAMES.CUSTOMER_REPORT]: HEALTHCHECK_LABELS.CUSTOMER_REPORT,
  [HEALTHCHECK_NAMES.CHANGE_REQUESTS]: HEALTHCHECK_LABELS.CHANGE_REQUESTS,
  [HEALTHCHECK_NAMES.STAKEHOLDERS]: HEALTHCHECK_LABELS.STAKEHOLDERS,
  [HEALTHCHECK_NAMES.STAFFING_RISKS]: HEALTHCHECK_LABELS.RISKS,
  [HEALTHCHECK_NAMES.PROJECT_UNDERSTAFFED]: HEALTHCHECK_LABELS.TEAM,
  [HEALTHCHECK_NAMES.KEY_ROLE_UNSTAFFED]: HEALTHCHECK_LABELS.ROLES,
  [HEALTHCHECK_NAMES.REPLACEMENT_NOT_ASSIGNED]: HEALTHCHECK_LABELS.REPLACEMENT,
  [HEALTHCHECK_NAMES.TEAM_QUALITY_MISMATCH]: HEALTHCHECK_LABELS.QUALITY,
};

type SubIssueSource = { name?: string; zone?: ControlZoneSource; status?: string; description?: string };

const healthcheckToSource = (c: Healthcheck): SubIssueSource => ({
  name: c.name,
  zone: c.targetZone,
  status: c.status,
  description: c.message,
});

const staffingIssueToSource = (i: StaffingRiskIssue): SubIssueSource => ({
  name: i.systemIssueType,
  zone: i.targetPriority,
  status: i.status,
  description: i.description,
});

const buildSubIssues = (items: SubIssueSource[], names: readonly string[]): SubIssue[] =>
  items
    .filter(({ status, name }) => status === HEALTH_STATUS.ACTIVE && !!name && names.includes(name!))
    .flatMap(({ name, zone: rawZone, description }) => {
      const label = HEALTHCHECK_NAME_TO_LABEL[name!];
      const zone = normalizeControlZone(rawZone ?? null);
      return label && zone && zone !== ZONE_KEYS.SAFE ? [{ label, zone, description }] : [];
    })
    .filter((s, i, arr) => arr.findIndex(x => x.label === s.label) === i);

const CONTROL_ISSUE_CONFIG: {
  key: ControlIssueKey;
  healthcheckNames: string[];
  staffingRiskIssueTypes?: string[];
}[] = [
  {
    key: 'commercial',
    healthcheckNames: [...commercialHealthcheckNames],
  },
  {
    key: 'projectManagement',
    healthcheckNames: [...projectManagementHealthcheckNames],
  },
  {
    key: 'engineering',
    healthcheckNames: [...engineeringHealthcheckNames],
  },
  {
    key: 'staffing',
    healthcheckNames: [...staffingHealthcheckNames],
    staffingRiskIssueTypes: [...staffingRiskIssueTypes],
  },
];

export const getControlIssues = (source?: ControlsIssueSource | null): ControlIssue[] => {
  const healthChecks = source?.health?.healthChecks || [];
  const staffingRiskIssues = source?.staffingRisk?.issues || [];
  const healthSources = healthChecks.map(healthcheckToSource);
  const staffingSources = staffingRiskIssues.map(staffingIssueToSource);

  return CONTROL_ISSUE_CONFIG.map(({ key, healthcheckNames, staffingRiskIssueTypes: issueTypes }) => {
    const zone = resolveControlsZone({
      healthChecks,
      healthcheckNames,
      staffingRiskIssues,
      staffingRiskIssueTypes: issueTypes,
    });
    const subIssues = [
      ...buildSubIssues(healthSources, healthcheckNames),
      ...(issueTypes ? buildSubIssues(staffingSources, issueTypes) : []),
    ];

    return {
      key,
      zone,
      message: CONTROL_HEALTH_MESSAGES[key][zone],
      subIssues,
    };
  });
};
